import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import StarIcon from '@mui/icons-material/Star';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { type FC, useMemo, useState } from 'react';
import { useBciCreateProposalMutation, useBciGetProductInfoQuery } from 'services/api/bciSeguros.api';
import type {
  BciDeductibleRate,
  BciProductInfo,
  BciProductQuotation,
  BciSegurosFormData,
} from 'services/types/bciSegurosForm';

import {
  backButtonSx,
  glassmorphismBackdropSx,
  glassmorphismPaperSx,
  NAVY,
  NAVY_SHADOW,
  SURFACE_CARD,
  SURFACE_LOW,
  secondaryButtonSx,
  YELLOW,
} from '../styleConstants';

interface QuoteResultsStepProps {
  formData: BciSegurosFormData;
  onFormDataChange: (updates: Partial<BciSegurosFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  setIsLoading: (loading: boolean) => void;
}

type DeductibleTag = { label: string; color: string; bgcolor: string } | null;

const getDeductibleTag = (ded: BciDeductibleRate, allDeductibles: BciDeductibleRate[]): DeductibleTag => {
  if (ded.dbl_ValorDeducibleUF === 0) {
    return { label: 'Sin deducible', color: NAVY, bgcolor: SURFACE_LOW };
  }
  const sorted = [...allDeductibles].sort((a, b) => a.dbl_ValorDeducibleUF - b.dbl_ValorDeducibleUF);
  const midIndex = Math.floor(sorted.length / 2);
  if (ded.int_IdDeducible === sorted[midIndex]?.int_IdDeducible) {
    return { label: 'Recomendado', color: '#2E7D32', bgcolor: '#E8F5E9' };
  }
  if (ded.int_IdDeducible === sorted[sorted.length - 1]?.int_IdDeducible) {
    return { label: 'Más económico', color: '#E65100', bgcolor: '#FFF3E0' };
  }
  return null;
};

const estimateUfValue = (deductibles: BciDeductibleRate[]): number | null => {
  const withUF = deductibles.find((d) => d.dbl_PrimaBrutaUF > 0 && d.dbl_PrimaBrutaPesos > 0);
  if (!withUF) return null;
  return Math.round(withUF.dbl_PrimaBrutaPesos / withUF.dbl_PrimaBrutaUF);
};

const ProductInfoPanel: FC<{ info: BciProductInfo; exclusions?: string[] }> = ({ info, exclusions }) => (
  <Box sx={{ px: 2.5, py: 2, bgcolor: SURFACE_LOW }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Chip
        label="Argumento de venta"
        size="small"
        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.7rem', height: 22 }}
      />
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 500, color: NAVY, mb: 2, fontStyle: 'italic' }}>
      &quot;{info.sellingPoint}&quot;
    </Typography>

    <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      Coberturas principales
    </Typography>
    <Box sx={{ mt: 0.5, mb: 3 }}>
      {info.highlights.map((h) => (
        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: YELLOW, mt: 0.3 }} />
          <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6 }}>
            {h}
          </Typography>
        </Box>
      ))}
    </Box>

    {exclusions && exclusions.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Qué no cubre
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {exclusions.map((e) => (
            <Box key={e} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
              <CloseIcon sx={{ fontSize: 14, color: '#C62828', mt: 0.3 }} />
              <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6 }}>
                {e}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    )}

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
          Asignación de taller
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: NAVY, opacity: 0.6 }}>
          {info.workshop}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY }}>
          Responsabilidad Civil
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: NAVY, opacity: 0.6 }}>
          {info.rcType}
        </Typography>
      </Box>
    </Box>

    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: SURFACE_CARD, borderRadius: '1rem' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#E65100' }}>
        Perfil del cliente:
      </Typography>
      <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6 }}>
        {' '}
        {info.targetClient}
      </Typography>
    </Box>
  </Box>
);

interface DeductibleRow {
  ufValue: number;
  label: string;
  cells: Map<number, BciDeductibleRate>;
}

const buildDeductibleRows = (products: BciProductQuotation[]): DeductibleRow[] => {
  const ufSet = new Map<number, { label: string; cells: Map<number, BciDeductibleRate> }>();

  for (const product of products) {
    for (const ded of product.lst_Deducibles) {
      if (!ufSet.has(ded.dbl_ValorDeducibleUF)) {
        ufSet.set(ded.dbl_ValorDeducibleUF, { label: ded.str_NombreDeducible, cells: new Map() });
      }
      ufSet.get(ded.dbl_ValorDeducibleUF)?.cells.set(product.int_CodigoProducto, ded);
    }
  }

  return [...ufSet.entries()]
    .sort(([a], [b]) => a - b)
    .map(([ufValue, { label, cells }]) => ({ ufValue, label, cells }));
};

const QuoteResultsStep: FC<QuoteResultsStepProps> = ({ formData, onFormDataChange, onNext, onBack, setIsLoading }) => {
  const [createProposal] = useBciCreateProposalMutation();
  const { data: productInfoData } = useBciGetProductInfoQuery();
  const productInfoMap = productInfoData?.products ?? {};
  const commonExclusions = productInfoData?.commonExclusions ?? [];
  const [coverageModalProduct, setCoverageModalProduct] = useState<BciProductQuotation | null>(null);
  const [proposalError, setProposalError] = useState<string | null>(null);

  const handleSelectDeductible = (productCode: number, deductibleId: number) => {
    onFormDataChange({
      quoteResult: {
        ...formData.quoteResult,
        selectedProductCode: productCode,
        selectedDeductibleId: deductibleId,
      },
    });
  };

  const handleGenerateProposal = async () => {
    if (!formData.quoteResult.selectedProductCode || !formData.quoteResult.selectedDeductibleId) return;

    const matchingQuote = formData.quoteResult.quoteMapping.find(
      (q) =>
        q.productCode === formData.quoteResult.selectedProductCode &&
        q.deductibleId === formData.quoteResult.selectedDeductibleId
    );

    if (!matchingQuote || !formData.quoteResult.leadId) return;

    setIsLoading(true);
    setProposalError(null);
    try {
      const result = await createProposal({
        str_NroCotizacion: formData.quoteResult.quoteNumber,
        Int_codigo_Producto: formData.quoteResult.selectedProductCode,
        int_IdDeducibleSeleccionado: formData.quoteResult.selectedDeductibleId,
        int_CantidadCuotas: formData.insuranceConfig.installmentCount,
        int_IdFormaPago: formData.insuranceConfig.paymentMethod,
        Asegurado: {
          Int_Rut: formData.insured.rut,
          Str_Dv: formData.insured.dv,
          Str_Nombres: formData.insured.firstName,
          Str_ApellidoPaterno: formData.insured.lastName,
          Str_ApellidoMaterno: formData.insured.secondLastName,
          Str_Email: formData.insured.email,
          Str_Celular: formData.insured.phone,
          Int_Edad: formData.insured.age,
        },
        Contratante: {
          Int_Rut: formData.contractor.rut,
          Str_Dv: formData.contractor.dv,
          Str_Nombres: formData.contractor.firstName,
          Str_ApellidoPaterno: formData.contractor.lastName,
          Str_ApellidoMaterno: formData.contractor.secondLastName,
          Str_Email: formData.contractor.email,
          Str_Celular: formData.contractor.phone,
          Int_IdRegion: formData.contractor.regionId,
          Int_IdComuna: formData.contractor.communeId,
          Str_Direccion: formData.contractor.address,
        },
        Vehiculo: {
          Str_Patente: formData.vehicle.licensePlate,
          Str_Motor: formData.vehicle.engine,
          Str_Chasis: formData.vehicle.chassis,
          Str_Color: formData.vehicle.color,
        },
        carInsuranceQuoteId: matchingQuote.quoteId,
        leadId: formData.quoteResult.leadId,
      }).unwrap();

      onFormDataChange({
        policy: {
          ...formData.policy,
          proposalPdf: result.str_Propuesta || null,
        },
      });

      onNext();
    } catch (error) {
      console.error('Error generando propuesta:', error);
      setProposalError('Ocurrió un error al generar la propuesta. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const { products } = formData.quoteResult;

  const ufValue = useMemo(() => {
    const match = products.find((product) => estimateUfValue(product.lst_Deducibles) !== null);
    return match ? estimateUfValue(match.lst_Deducibles) : null;
  }, [products]);

  const deductibleRows = useMemo(() => buildDeductibleRows(products), [products]);

  const productCount = products.length;
  const columnWidth = '1fr';
  const gridColumns = `180px repeat(${productCount}, ${columnWidth})`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>
          Resultados de Cotización
        </Typography>
        <Chip
          label={`N° ${formData.quoteResult.quoteNumber}`}
          size="small"
          sx={{ mt: 1, bgcolor: SURFACE_LOW, color: NAVY, fontWeight: 600 }}
        />
      </Box>
      {products.length === 0 && (
        <Typography sx={{ color: NAVY, opacity: 0.6, textAlign: 'center', py: 4 }}>
          No se encontraron productos disponibles.
        </Typography>
      )}
      {products.length > 0 && (
        <Box
          sx={{
            borderRadius: '1.5rem',
            overflow: 'hidden',
            boxShadow: NAVY_SHADOW,
          }}
        >
          {/* Product column headers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: gridColumns,
              bgcolor: NAVY,
            }}
          >
            {/* Empty corner cell */}
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Deducible
              </Typography>
            </Box>

            {/* Product headers — no border, just spacing */}
            {products.map((product) => {
              const info = productInfoMap[product.str_NombreProducto];
              return (
                <Box
                  key={product.int_CodigoProducto}
                  sx={{
                    px: 2,
                    py: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                    {product.str_NombreProducto}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.25 }}>
                    {info?.tagline || 'Seguro Automotriz'}
                  </Typography>
                  {info && (
                    <Button
                      size="small"
                      onClick={() => setCoverageModalProduct(product)}
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        textTransform: 'none',
                        fontSize: '0.68rem',
                        p: 0,
                        mt: 0.5,
                        minWidth: 0,
                        '&:hover': { color: '#fff', bgcolor: 'transparent' },
                      }}
                    >
                      Ver coberturas
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Deductible rows */}
          {deductibleRows.map((row, rowIndex) => {
            const firstProductDeductibles = products[0]?.lst_Deducibles || [];
            const firstDed = row.cells.values().next().value as BciDeductibleRate | undefined;
            const tag = firstDed ? getDeductibleTag(firstDed, firstProductDeductibles) : null;
            const isRecommended = tag?.label === 'Recomendado';
            const deductibleCLP = ufValue && row.ufValue > 0 ? Math.round(row.ufValue * ufValue) : null;

            return (
              <Box
                key={row.ufValue}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridColumns,
                  bgcolor: isRecommended ? 'rgba(46,125,50,0.02)' : rowIndex % 2 === 0 ? SURFACE_CARD : SURFACE_LOW,
                  transition: 'background-color 0.15s ease',
                }}
              >
                {/* Deductible label cell */}
                <Box
                  sx={{
                    px: 2,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    borderLeft: isRecommended ? `3px solid ${YELLOW}` : '3px solid transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                      {row.label}
                    </Typography>
                    {tag && (
                      <Chip
                        label={tag.label}
                        size="small"
                        icon={isRecommended ? <StarIcon sx={{ fontSize: '12px !important' }} /> : undefined}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          color: tag.color,
                          bgcolor: tag.bgcolor,
                          '& .MuiChip-icon': { color: tag.color },
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6, mt: 0.25, fontSize: '0.68rem' }}>
                    {row.ufValue} UF
                    {deductibleCLP ? ` (~$${deductibleCLP.toLocaleString('es-CL')})` : ''}
                    {row.ufValue === 0 ? ' (sin costo)' : ''}
                  </Typography>
                </Box>
                {/* Price cells per product — no borders, tonal separation only */}
                {products.map((product) => {
                  const ded = row.cells.get(product.int_CodigoProducto);
                  if (!ded) {
                    return (
                      <Box
                        key={product.int_CodigoProducto}
                        sx={{
                          px: 2,
                          py: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: NAVY, opacity: 0.4 }}>
                          —
                        </Typography>
                      </Box>
                    );
                  }

                  const isSelected =
                    formData.quoteResult.selectedProductCode === product.int_CodigoProducto &&
                    formData.quoteResult.selectedDeductibleId === ded.int_IdDeducible;

                  return (
                    <Box
                      key={product.int_CodigoProducto}
                      onClick={() => handleSelectDeductible(product.int_CodigoProducto, ded.int_IdDeducible)}
                      sx={{
                        px: 2,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(249, 208, 10, 0.08)' : 'transparent',
                        transition: 'all 0.2s ease',
                        transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                        '&:hover': { bgcolor: isSelected ? 'rgba(249, 208, 10, 0.12)' : 'rgba(249, 208, 10, 0.04)' },
                      }}
                    >
                      <Radio
                        checked={isSelected}
                        size="small"
                        sx={{
                          p: 0.5,
                          color: YELLOW,
                          '&.Mui-checked': { color: YELLOW },
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: NAVY, lineHeight: 1.2, fontSize: '0.95rem' }}
                        >
                          ${ded.dbl_CuotaPesos.toLocaleString('es-CL')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6, fontSize: '0.68rem' }}>
                          /cuota · {ded.dbl_PrimaBrutaUF.toFixed(2)} UF
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      )}
      {proposalError && (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ borderRadius: '1.5rem', mt: 2 }}>
          {proposalError}
        </Alert>
      )}
      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onBack} startIcon={<NavigateBeforeIcon />} sx={backButtonSx}>
          Atrás
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleGenerateProposal().catch(() => {});
          }}
          disabled={!formData.quoteResult.selectedProductCode || !formData.quoteResult.selectedDeductibleId}
          startIcon={<DescriptionIcon />}
          sx={secondaryButtonSx}
        >
          Generar Propuesta
        </Button>
      </Box>
      {/* Coverage details modal */}
      <Dialog
        open={coverageModalProduct !== null}
        onClose={() => setCoverageModalProduct(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: glassmorphismBackdropSx,
          },
          paper: {
            sx: glassmorphismPaperSx,
          },
        }}
      >
        {coverageModalProduct &&
          (() => {
            const info = productInfoMap[coverageModalProduct.str_NombreProducto];
            return (
              <>
                <Box
                  sx={{
                    bgcolor: NAVY,
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                      {coverageModalProduct.str_NombreProducto}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      {info?.tagline || 'Seguro Automotriz'}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setCoverageModalProduct(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                {info && (
                  <Box sx={{ p: 0 }}>
                    <ProductInfoPanel info={info} exclusions={commonExclusions} />
                  </Box>
                )}
              </>
            );
          })()}
      </Dialog>
    </Box>
  );
};

export default QuoteResultsStep;
